import { useQuery } from "@tanstack/react-query";
import { getJson, PRC_PREFIX } from "./client";
import type { ConcentrationResponse, LimitBasis } from "./types";

/**
 * The measurement BASIS: which quantity every concentration is measured in.
 *
 * ── NOT the same thing as `LimitBasis` ──────────────────────────────────────────────────
 * `LimitBasis` is "approved | proposed" -- whether pending proposals are applied. This is
 * "net investment | present value" -- which money column the numerator and denominator are
 * both taken from. Two unrelated axes that unfortunately both read as "basis" in English,
 * which is exactly why the query parameter is `valueBasis` and never `basis`.
 *
 * ── Not the same as a limit's `measure` either ──────────────────────────────────────────
 * `measure` is a property of ONE limit ("this one totals unguaranteed residuals"). A basis
 * is a property of the whole read. A limit naming its own measure keeps it under either
 * basis -- only the default measure follows the basis.
 */
export type ValueBasis = "net_investment" | "present_value" | "fair_market_value";

/** What the API returns when `valueBasis` is left out of a request, so the request helpers below omit it for
 *  this one. Pages with no basis dropdown (e.g. Attestation) rely on it. NOT what the dropdowns start on. */
export const DEFAULT_VALUE_BASIS: ValueBasis = "net_investment";

/** The basis every basis dropdown starts on: Monitor, Capital and Scenario Portfolio. It is always sent
 *  explicitly, since the API's own default is net investment. */
export const UI_DEFAULT_VALUE_BASIS: ValueBasis = "present_value";

/** Column headings and filter labels. Every dollar figure on screen is in one of these.
 *  "Book Value" -- not "Net investment" -- because that is the business's own name for this
 *  basis (sql/47_'s header: "Book Value IS net investment: there is no separate book-value
 *  column"), and it is what Scenario Portfolio's own Measured-in control already calls it. */
export const VALUE_BASIS_LABEL: Record<ValueBasis, string> = {
  net_investment: "Book Value",
  present_value: "Present Value ( DCB)",
  fair_market_value: "Fair market value (FMV)",
};

export async function fetchConcentration(
  facilityCode: string,
  asOfDate: string,
  includeJoints = false,
  basis: LimitBasis = "approved",
  investor = "WAFRA",
  book = "ALL",
  product = "ALL",
  valueBasis: ValueBasis = DEFAULT_VALUE_BASIS,
): Promise<ConcentrationResponse> {
  const params = new URLSearchParams({ facility: facilityCode, as_of: asOfDate });
  if (includeJoints) params.set("include_joints", "1");
  // Omitted for Wafra for the same reason `basis` is omitted when approved: an unchanged
  // request URL for the default case, and no second cache entry for the same rows.
  if (investor !== "WAFRA") params.set("investor", investor);
  // Omitted on the approved basis, so the request is byte-identical to what it was before
  // the proposed basis existed and nothing is cached under a different key for no reason.
  if (basis !== "approved") params.set("basis", basis);
  // Sent on every scope, including Internal. It narrows nothing there yet — prc_limit_config
  // has no book dimension — but the wiring is what makes the filter work the day it does.
  if (book && book !== "ALL") params.set("book", book);
  // Independent of `book`; the API intersects the two. See PRODUCT_FILTERS.
  if (product && product !== "ALL") params.set("product", product);
  // `valueBasis`, NOT `basis` -- the backend already has a `basis` parameter meaning
  // approved/proposed. Omitted on the default so the request URL is unchanged.
  if (valueBasis !== DEFAULT_VALUE_BASIS) params.set("valueBasis", valueBasis);
  return getJson<ConcentrationResponse>(`${PRC_PREFIX}concentration?${params.toString()}`);
}

export function useConcentration(
  facilityCode: string,
  asOfDate: string,
  includeJoints = false,
  basis: LimitBasis = "approved",
  investor = "WAFRA",
  book = "ALL",
  product = "ALL",
  valueBasis: ValueBasis = DEFAULT_VALUE_BASIS,
) {
  return useQuery({
    // valueBasis is part of the key: the two bases are different numbers for the same
    // limits, and sharing a cache entry would show one under the other's heading.
    queryKey: [
      "prc-concentration", facilityCode, asOfDate, includeJoints, basis, investor, book,
      product, valueBasis,
    ],
    queryFn: () =>
      fetchConcentration(facilityCode, asOfDate, includeJoints, basis, investor, book,
                         product, valueBasis),
    enabled: !!facilityCode,
  });
}
